import {
	ArrowLeft,
	Check,
	Copy,
	ExternalLink,
	Phone,
	RefreshCw,
	Trash2,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { fetchJson, sendJson } from "../api/rest";
import {
	btn,
	Empty,
	ErrorBanner,
	fmtNumber,
	fmtRelative,
	input,
	kr,
	Panel,
	Spinner,
} from "../components/flows/ui";
import { PageHeader } from "../components/layout/PageHeader";
import {
	compact,
	NETWORK_LABEL,
	nextActionLabel,
	OUTCOME_LABEL,
	PLATFORM_LABEL,
	RESEARCH_LABEL,
	STATUS_LABEL,
	StatusDot,
} from "../components/prospects/labels";
import {
	type CallOutcome,
	type ProspectActivity,
	type ProspectDetail,
	type ProspectStatus,
	type Result,
	storefrontProductUrl,
} from "../types/prospects";

const OUTCOMES: CallOutcome[] = [
	"no_answer",
	"callback",
	"interested",
	"not_interested",
	"wrong_person",
	"signed_up",
	"do_not_contact",
];

const STATUSES: ProspectStatus[] = [
	"new",
	"to_call",
	"callback",
	"interested",
	"not_interested",
	"signed_up",
	"do_not_contact",
	"parked",
];

const OUTCOME_STYLE: Record<CallOutcome, string> = {
	no_answer: "border-zinc-300",
	callback: "border-amber-300",
	interested: "border-emerald-400",
	not_interested: "border-rose-300",
	wrong_person: "border-sky-300",
	signed_up: "border-green-600",
	do_not_contact: "border-zinc-700",
};

const errorText = (err: unknown) =>
	err instanceof Error ? err.message : String(err);

const toLocalInput = (d: Date) => {
	const pad = (n: number) => String(n).padStart(2, "0");
	return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const at = (days: number, hour = 10) => {
	const d = new Date();
	d.setDate(d.getDate() + days);
	d.setHours(hour, 0, 0, 0);
	return d;
};

const nextMonday = () => {
	const d = new Date();
	d.setDate(d.getDate() + ((8 - d.getDay()) % 7 || 7));
	d.setHours(10, 0, 0, 0);
	return d;
};

const QUICK_PICKS: { label: string; date: () => Date }[] = [
	{ label: "In 2 hours", date: () => new Date(Date.now() + 2 * 3_600_000) },
	{ label: "Tomorrow 10:00", date: () => at(1) },
	{ label: "In 3 days", date: () => at(3) },
	{ label: "Next Monday", date: nextMonday },
	{ label: "In 2 weeks", date: () => at(14) },
];

const n = (v: number | null | undefined) => fmtNumber(v ?? 0);

function talkingPoints(p: ProspectDetail): string[] {
	const points: string[] = [];
	if (p.lane === "winback" && p.impressions) {
		points.push(
			`I har været hos os før. Jeres produkter er blevet vist ${n(p.impressions)} gange på Google de sidste 28 dage, mens I ikke har været med.`,
		);
	} else if (p.sharedEstimate) {
		points.push(
			`Vi har allerede sider for cirka ${n(p.sharedEstimate)} af de produkter, I sælger. ${p.impressions ? `De blev vist ${n(p.impressions)} gange på Google de sidste 28 dage.` : ""}`.trim(),
		);
	}
	if (p.unlockEstimate) {
		points.push(
			`På cirka ${n(p.unlockEstimate)} af dem har vi kun én butik eller ingen i dag. Med jer kan kunderne sammenligne priser der, og så er det jer, de møder.`,
		);
	}
	if (p.comparedCount >= 3 && p.cheaperCount > 0) {
		points.push(
			`I vores stikprøve var I billigere end den laveste pris hos os på ${p.cheaperCount} af ${p.comparedCount} produkter.`,
		);
	}
	if (p.lane === "new" && p.source === "pricerunner") {
		points.push(
			"I er allerede på PriceRunner, så I har et produktfeed. Det samme feed kan bruges hos os.",
		);
	}
	if (p.platform === "shopify") {
		points.push("I kører på Shopify, så feedet kan hentes direkte derfra.");
	} else if (p.platform === "woocommerce") {
		points.push("I kører på WooCommerce, så et feed-plugin kan levere feedet.");
	} else if (p.platform?.startsWith("dandomain")) {
		points.push("DanDomain kan eksportere et produktfeed, som vi kan læse.");
	}
	const signals = (p.facts?.signals ?? {}) as { google_ads?: boolean };
	if (signals.google_ads) {
		points.push(
			"I annoncerer på Google. Som certificeret CSS-partner kan vi også hjælpe med jeres Shopping-annoncer.",
		);
	}
	if (p.network) {
		points.push(
			`I bruger ${NETWORK_LABEL[p.network] ?? p.network}. Vi kan også liste jer der og blive betalt per salg.`,
		);
	}
	return points;
}

const CopyButton = ({ text }: { text: string }) => {
	const [done, setDone] = useState(false);
	return (
		<button
			type="button"
			className="text-zinc-400 hover:text-zinc-700"
			title="Copy"
			onClick={() => {
				navigator.clipboard.writeText(text).then(() => {
					setDone(true);
					setTimeout(() => setDone(false), 1200);
				});
			}}
		>
			{done ? (
				<Check className="h-3.5 w-3.5" />
			) : (
				<Copy className="h-3.5 w-3.5" />
			)}
		</button>
	);
};

const Stat = ({
	label,
	value,
	hint,
}: {
	label: string;
	value: string;
	hint?: string;
}) => (
	<div>
		<div className="text-[11px] font-medium uppercase tracking-wide text-zinc-500">
			{label}
		</div>
		<div className="mt-1 text-[22px] font-semibold leading-tight tabular-nums text-ink">
			{value}
		</div>
		{hint && <div className="text-[11px] text-zinc-400">{hint}</div>}
	</div>
);

const ActivityItem = ({
	a,
	onDelete,
}: {
	a: ProspectActivity;
	onDelete: () => void;
}) => {
	const label =
		a.kind === "call"
			? `Call · ${OUTCOME_LABEL[a.outcome as CallOutcome] ?? a.outcome}`
			: a.kind === "status"
				? "Status"
				: a.kind === "research"
					? "Research"
					: "Note";
	const next = nextActionLabel(a.nextActionAt);
	return (
		<li className="relative border-l border-hairline pb-4 pl-4 last:pb-0">
			<span
				className={`absolute top-1.5 -left-[4.5px] h-2 w-2 rounded-full ${a.kind === "call" ? "bg-ink" : a.kind === "research" ? "bg-zinc-200" : "bg-zinc-400"}`}
			/>
			<div className="flex items-baseline justify-between gap-2">
				<span className="text-[12px] font-semibold text-ink">{label}</span>
				<span className="shrink-0 text-[11px] text-zinc-400">
					{fmtRelative(a.createdAt)}
				</span>
			</div>
			{a.body && (
				<p
					className={`mt-0.5 whitespace-pre-wrap text-[13px] ${a.kind === "research" ? "text-zinc-400" : "text-zinc-700"}`}
				>
					{a.body}
				</p>
			)}
			<div className="mt-0.5 flex flex-wrap gap-x-3 text-[11px] text-zinc-400">
				{a.createdBy && (
					<span>{a.createdBy.replace(/@priceonline\.dk$/, "")}</span>
				)}
				{next && <span>Next: {next.text}</span>}
				{a.emailConsent && (
					<span className="text-emerald-700">OK to email</span>
				)}
				{a.kind === "note" && (
					<button
						type="button"
						className="text-zinc-400 hover:text-rose-600"
						onClick={onDelete}
						title="Delete note"
					>
						<Trash2 className="inline h-3 w-3" />
					</button>
				)}
			</div>
		</li>
	);
};

export default function ColdCall() {
	const { id } = useParams();
	const [p, setP] = useState<ProspectDetail | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [notice, setNotice] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	const [outcome, setOutcome] = useState<CallOutcome | null>(null);
	const [note, setNote] = useState("");
	const [nextAt, setNextAt] = useState("");
	const [consent, setConsent] = useState(false);
	const [editing, setEditing] = useState(false);
	const [contact, setContact] = useState({
		contactName: "",
		phone: "",
		email: "",
		domain: "",
	});
	const [showAllMatches, setShowAllMatches] = useState(false);

	const load = useCallback(async () => {
		try {
			const data = await fetchJson<ProspectDetail>(`/prospects/${id}`);
			setP(data);
			setContact({
				contactName: data.contactName ?? "",
				phone: data.phone ?? "",
				email: data.email ?? "",
				domain: data.domain ?? "",
			});
		} catch (err) {
			setError(errorText(err));
		}
	}, [id]);

	useEffect(() => {
		load();
	}, [load]);

	const act = async (fn: () => Promise<Result>, done: string) => {
		setBusy(true);
		setError(null);
		try {
			const res = await fn();
			if (!res.success) throw new Error(res.error ?? "Failed");
			setNotice(done);
			await load();
			return true;
		} catch (err) {
			setError(errorText(err));
			return false;
		} finally {
			setBusy(false);
		}
	};

	const logCall = async () => {
		if (!outcome && !note.trim()) return;
		const ok = await act(
			() =>
				sendJson<Result>(`/prospects/${id}/activities`, "POST", {
					kind: outcome ? "call" : "note",
					outcome: outcome ?? undefined,
					body: note.trim() || undefined,
					nextActionAt: nextAt ? new Date(nextAt).toISOString() : undefined,
					emailConsent: consent,
				}),
			outcome ? `Call logged: ${OUTCOME_LABEL[outcome]}.` : "Note added.",
		);
		if (ok) {
			setOutcome(null);
			setNote("");
			setNextAt("");
			setConsent(false);
		}
	};

	const research = () =>
		act(async () => {
			const res = await sendJson<{
				success: boolean;
				error?: string;
				runId?: number;
			}>(`/prospects/${id}/research`, "POST");
			return res;
		}, "Research started. Reload in a minute or two.");

	const points = useMemo(() => (p ? talkingPoints(p) : []), [p]);
	const pages = p?.products.filter((r) => r.kind === "page") ?? [];
	const matches = p?.products.filter((r) => r.kind === "slug") ?? [];
	const shownMatches = showAllMatches ? matches : matches.slice(0, 15);

	if (!p && !error) return <Spinner />;
	if (!p)
		return (
			<div className="space-y-4">
				<Link to="/cold-calls" className={btn.ghost}>
					<ArrowLeft className="h-3.5 w-3.5" /> Cold calls
				</Link>
				<ErrorBanner error={error} />
			</div>
		);

	const signals = (p.facts?.signals ?? {}) as {
		title?: string;
		lang?: string;
		google_ads?: boolean;
		gtm?: boolean;
		meta_pixel?: boolean;
		payment_methods?: string[];
		trust_marks?: string[];
		other_networks?: string[];
	};
	const sample = (p.facts?.sample ?? {}) as {
		precision?: number;
		precisionMeasured?: boolean;
		jsonLdPages?: number;
		productPages?: number;
	};
	const sitemapFacts = (p.facts?.sitemap ?? {}) as {
		source?: string;
		files?: number;
	};
	const next = nextActionLabel(p.nextActionAt);

	return (
		<div className="space-y-6">
			<PageHeader
				kicker={
					<Link to="/cold-calls" className="hover:underline">
						Sales · Cold calls
					</Link>
				}
				title={`${p.name}.`}
				qualifier={p.domain ?? RESEARCH_LABEL[p.researchStatus]}
				description={
					<div className="flex flex-wrap items-center gap-x-4 gap-y-1">
						<StatusDot status={p.status} />
						{next && (
							<span className={next.late ? "text-rose-600" : ""}>
								Next action {next.text}
							</span>
						)}
						{p.score !== null && (
							<span>Score {compact(Math.round(p.score))}</span>
						)}
						<span>
							{RESEARCH_LABEL[p.researchStatus]}
							{p.researchedAt ? ` ${fmtRelative(p.researchedAt)}` : ""}
						</span>
					</div>
				}
				actions={
					<>
						<select
							className={input.replace("w-full ", "")}
							value={p.status}
							disabled={busy}
							onChange={(e) =>
								act(
									() =>
										sendJson<Result>(`/prospects/${id}`, "PATCH", {
											status: e.target.value,
										}),
									"Status changed.",
								)
							}
						>
							{STATUSES.map((s) => (
								<option key={s} value={s}>
									{STATUS_LABEL[s]}
								</option>
							))}
						</select>
						<button
							type="button"
							className={btn.ghost}
							disabled={busy}
							onClick={research}
						>
							<RefreshCw className="h-3.5 w-3.5" />
							Research again
						</button>
					</>
				}
			/>
			<ErrorBanner error={error} />
			{notice && (
				<div className="rounded-xl border border-hairline bg-white px-4 py-3 text-[13px] text-zinc-600">
					{notice}
				</div>
			)}

			{p.merchant && (
				<div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] text-amber-900">
					Former merchant:{" "}
					<Link
						to={`/merchants/${p.merchant.id}`}
						className="font-medium underline"
					>
						{p.merchant.name}
					</Link>{" "}
					({p.merchant.tier}, {p.merchant.validated ? "listed" : "not listed"}).
					Their feed may still be in the webapp, so this is a win-back call.
				</div>
			)}
			{p.network && (
				<div className="rounded-xl border border-hairline bg-white px-4 py-3 text-[13px] text-zinc-700">
					The site loads {NETWORK_LABEL[p.network] ?? p.network} tracking.
					Applying to their programme lists them at no cost to them, which may
					be the easier first step.
				</div>
			)}
			{p.isDanish === false && (
				<div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] text-rose-800">
					This does not look like a Danish shop (no Danish language, .dk domain
					or CVR with kroner prices).
				</div>
			)}

			<div className="grid gap-6 xl:grid-cols-12">
				<div className="space-y-6 xl:col-span-8">
					<Panel
						title="Why call"
						hint="Estimated from a sample of their catalogue"
					>
						{p.researchStatus === "sampled" ? (
							<>
								<div className="grid grid-cols-2 gap-5 md:grid-cols-5">
									<Stat
										label="Shared"
										value={compact(p.sharedEstimate)}
										hint="their products we list"
									/>
									<Stat
										label="Impressions"
										value={compact(p.impressions)}
										hint="on Google, 28 days"
									/>
									<Stat
										label="0–1 shops"
										value={compact(p.unlockEstimate)}
										hint="they would be 1st or 2nd"
									/>
									<Stat
										label="Cheaper"
										value={
											p.comparedCount
												? `${p.cheaperCount}/${p.comparedCount}`
												: "—"
										}
										hint="of sampled products"
									/>
									<Stat
										label="Catalogue"
										value={compact(p.catalogueSize)}
										hint={
											p.catalogueSize ? "products in sitemap" : "no sitemap"
										}
									/>
								</div>
								{p.categories.length > 0 && (
									<div className="mt-5 space-y-1.5">
										{p.categories.map((c) => (
											<div
												key={c.name}
												className="flex items-center gap-3 text-[12px]"
											>
												<span className="w-64 shrink-0 truncate text-zinc-600">
													{c.name}
												</span>
												<div className="h-1.5 flex-1 rounded-full bg-zinc-100">
													<div
														className="h-1.5 rounded-full bg-zinc-800"
														style={{ width: `${Math.round(c.share * 100)}%` }}
													/>
												</div>
												<span className="w-10 text-right tabular-nums text-zinc-500">
													{Math.round(c.share * 100)} %
												</span>
											</div>
										))}
									</div>
								)}
							</>
						) : (
							<Empty>
								{p.researchStatus === "no_domain"
									? "We could not find their website from the name. Add it under Contact, then research again."
									: p.researchStatus === "unreachable"
										? `The site did not answer${p.researchError ? ` (${p.researchError})` : ""}.`
										: p.researchStatus === "probed"
											? "We read the homepage but found no product pages to compare."
											: "Not researched yet."}
							</Empty>
						)}
					</Panel>

					{points.length > 0 && (
						<Panel title="Talking points" hint="Danish, from the numbers above">
							<ul className="space-y-2">
								{points.map((t) => (
									<li key={t} className="flex gap-2 text-[13px] text-zinc-700">
										<span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />
										{t}
									</li>
								))}
							</ul>
						</Panel>
					)}

					<Panel
						title="Evidence"
						hint={`${pages.length} product pages read on their site`}
					>
						{pages.length === 0 ? (
							<Empty>No product pages read.</Empty>
						) : (
							<div className="overflow-x-auto">
								<table className="w-full min-w-[720px] text-[12px]">
									<thead>
										<tr className="border-b border-hairline text-left text-[11px] uppercase tracking-wide text-zinc-500">
											<th className="py-2 pr-3 font-medium">Their page</th>
											<th className="py-2 pr-3 font-medium">Match</th>
											<th className="py-2 pr-3 text-right font-medium">
												Their price
											</th>
											<th className="py-2 pr-3 text-right font-medium">
												Our lowest
											</th>
											<th className="py-2 pr-3 text-right font-medium">
												Shops
											</th>
											<th className="py-2 pr-3 text-right font-medium">
												Impr.
											</th>
											<th className="py-2 font-medium">Our page</th>
										</tr>
									</thead>
									<tbody>
										{pages.map((r) => {
											const cheaper =
												r.price !== null &&
												r.ourLowest !== null &&
												r.price + 0.5 < r.ourLowest;
											return (
												<tr
													key={r.id}
													className="border-b border-hairline last:border-b-0"
												>
													<td className="max-w-[280px] py-1.5 pr-3">
														<a
															href={r.url}
															target="_blank"
															rel="noreferrer"
															className="block truncate text-ink hover:underline"
															title={r.url}
														>
															{r.name ?? r.url.replace(/^https?:\/\/[^/]+/, "")}
														</a>
													</td>
													<td className="py-1.5 pr-3 whitespace-nowrap">
														{r.matchKind === "gtin" ? (
															<span className="text-emerald-700">GTIN ✓</span>
														) : r.matchKind === "slug" ? (
															<span className="text-zinc-500">name</span>
														) : (
															<span className="text-zinc-300">none</span>
														)}
													</td>
													<td
														className={`py-1.5 pr-3 text-right tabular-nums ${cheaper ? "font-semibold text-emerald-700" : ""}`}
													>
														{r.price === null ? "—" : kr(r.price)}
														{r.currency && r.currency !== "DKK"
															? ` ${r.currency}`
															: ""}
													</td>
													<td className="py-1.5 pr-3 text-right tabular-nums">
														{kr(r.ourLowest)}
													</td>
													<td className="py-1.5 pr-3 text-right tabular-nums">
														{r.shops ?? "—"}
													</td>
													<td className="py-1.5 pr-3 text-right tabular-nums">
														{compact(r.impressions)}
													</td>
													<td className="max-w-[220px] truncate py-1.5">
														{r.productSlug ? (
															<a
																href={storefrontProductUrl(r.productSlug)}
																target="_blank"
																rel="noreferrer"
																className="inline-flex items-center gap-1 text-zinc-600 hover:underline"
															>
																{r.productName ?? r.productSlug}
																<ExternalLink className="h-2.5 w-2.5" />
															</a>
														) : r.productId ? (
															<Link
																to={`/products/${r.productId}`}
																className="text-zinc-600 hover:underline"
															>
																#{r.productId}
															</Link>
														) : (
															"—"
														)}
													</td>
												</tr>
											);
										})}
									</tbody>
								</table>
							</div>
						)}
					</Panel>

					<Panel
						title="Matched by name"
						hint={`${p.slugMatches} of ${p.sampledUrls} sampled catalogue URLs matched a product of ours, most searched first`}
					>
						{matches.length === 0 ? (
							<Empty>No catalogue URL matched one of our products.</Empty>
						) : (
							<>
								<table className="w-full text-[12px]">
									<thead>
										<tr className="border-b border-hairline text-left text-[11px] uppercase tracking-wide text-zinc-500">
											<th className="py-2 pr-3 font-medium">Our product</th>
											<th className="py-2 pr-3 font-medium">Category</th>
											<th className="py-2 pr-3 text-right font-medium">
												Shops
											</th>
											<th className="py-2 pr-3 text-right font-medium">
												Impr.
											</th>
											<th className="py-2 pr-3 text-right font-medium">
												Lowest
											</th>
											<th className="py-2 font-medium">Their page</th>
										</tr>
									</thead>
									<tbody>
										{shownMatches.map((r) => (
											<tr
												key={r.id}
												className="border-b border-hairline last:border-b-0"
											>
												<td className="max-w-[300px] truncate py-1.5 pr-3">
													{r.productSlug ? (
														<a
															href={storefrontProductUrl(r.productSlug)}
															target="_blank"
															rel="noreferrer"
															className="text-ink hover:underline"
														>
															{r.productName ?? r.productSlug}
														</a>
													) : (
														`#${r.productId}`
													)}
												</td>
												<td className="max-w-[180px] truncate py-1.5 pr-3 text-zinc-500">
													{r.category ?? "—"}
												</td>
												<td
													className={`py-1.5 pr-3 text-right tabular-nums ${r.shops !== null && r.shops <= 1 ? "font-semibold text-ink" : ""}`}
												>
													{r.shops ?? "—"}
												</td>
												<td className="py-1.5 pr-3 text-right tabular-nums">
													{compact(r.impressions)}
												</td>
												<td className="py-1.5 pr-3 text-right tabular-nums">
													{kr(r.ourLowest)}
												</td>
												<td className="max-w-[200px] truncate py-1.5">
													<a
														href={r.url}
														target="_blank"
														rel="noreferrer"
														className="text-zinc-500 hover:underline"
													>
														{r.url.replace(/^https?:\/\/[^/]+/, "")}
													</a>
												</td>
											</tr>
										))}
									</tbody>
								</table>
								{matches.length > 15 && (
									<button
										type="button"
										className="mt-3 text-[12px] text-zinc-500 hover:underline"
										onClick={() => setShowAllMatches(!showAllMatches)}
									>
										{showAllMatches
											? "Show fewer"
											: `Show all ${matches.length}`}
									</button>
								)}
							</>
						)}
					</Panel>
				</div>

				<div className="space-y-6 xl:col-span-4">
					<Panel
						title="Contact"
						action={
							<button
								type="button"
								className="text-[12px] text-zinc-500 hover:underline"
								onClick={() => setEditing(!editing)}
							>
								{editing ? "Cancel" : "Edit"}
							</button>
						}
					>
						{editing ? (
							<form
								className="space-y-2"
								onSubmit={async (e) => {
									e.preventDefault();
									const domainChanged =
										(contact.domain || "") !== (p.domain ?? "");
									const ok = await act(
										() =>
											sendJson<Result>(`/prospects/${id}`, "PATCH", {
												contactName: contact.contactName,
												phone: contact.phone,
												email: contact.email,
												...(domainChanged ? { domain: contact.domain } : {}),
											}),
										domainChanged
											? "Saved. The new website is queued for research."
											: "Contact saved.",
									);
									if (ok) setEditing(false);
								}}
							>
								{(
									[
										["contactName", "Contact person"],
										["phone", "Phone"],
										["email", "Email"],
										["domain", "Website"],
									] as const
								).map(([key, label]) => (
									<label key={key} className="block text-[11px] text-zinc-500">
										{label}
										<input
											className={`${input} mt-0.5`}
											value={contact[key]}
											onChange={(e) =>
												setContact({ ...contact, [key]: e.target.value })
											}
										/>
									</label>
								))}
								<button type="submit" className={btn.primary} disabled={busy}>
									Save
								</button>
							</form>
						) : (
							<dl className="space-y-2.5 text-[13px]">
								<div>
									<dt className="text-[11px] text-zinc-500">Phone</dt>
									<dd className="flex items-center gap-2">
										{p.phone ? (
											<>
												<a
													href={`tel:${p.phone.replace(/\s/g, "")}`}
													className="inline-flex items-center gap-1.5 text-[16px] font-semibold tabular-nums text-ink hover:underline"
												>
													<Phone className="h-4 w-4" />
													{p.phone}
												</a>
												<CopyButton text={p.phone} />
											</>
										) : (
											<span className="text-zinc-400">
												Not found. Add it with Edit.
											</span>
										)}
									</dd>
								</div>
								<div>
									<dt className="text-[11px] text-zinc-500">Contact person</dt>
									<dd>
										{p.contactName ?? <span className="text-zinc-400">—</span>}
									</dd>
								</div>
								<div>
									<dt className="text-[11px] text-zinc-500">Email</dt>
									<dd className="flex items-center gap-2">
										{p.email ? (
											<>
												<span className="truncate">{p.email}</span>
												<CopyButton text={p.email} />
											</>
										) : (
											<span className="text-zinc-400">—</span>
										)}
									</dd>
								</div>
								<div>
									<dt className="text-[11px] text-zinc-500">Website</dt>
									<dd>
										{p.domain ? (
											<a
												href={`https://${p.domain}`}
												target="_blank"
												rel="noreferrer"
												className="inline-flex items-center gap-1 hover:underline"
											>
												{p.domain}
												<ExternalLink className="h-3 w-3" />
											</a>
										) : (
											<span className="text-zinc-400">Unknown</span>
										)}
									</dd>
								</div>
								{p.cvr && (
									<div>
										<dt className="text-[11px] text-zinc-500">CVR</dt>
										<dd>
											<a
												href={`https://datacvr.virk.dk/enhed/virksomhed/${p.cvr}`}
												target="_blank"
												rel="noreferrer"
												className="inline-flex items-center gap-1 tabular-nums hover:underline"
											>
												{p.cvr}
												<ExternalLink className="h-3 w-3" />
											</a>
											<span className="ml-2 text-[11px] text-zinc-400">
												check for reklamebeskyttelse
											</span>
										</dd>
									</div>
								)}
								{p.sourceUrl && (
									<div>
										<dt className="text-[11px] text-zinc-500">Source</dt>
										<dd>
											<a
												href={p.sourceUrl}
												target="_blank"
												rel="noreferrer"
												className="inline-flex items-center gap-1 text-zinc-600 hover:underline"
											>
												PriceRunner listing
												<ExternalLink className="h-3 w-3" />
											</a>
										</dd>
									</div>
								)}
							</dl>
						)}
					</Panel>

					<Panel title="Log a call">
						<div className="flex flex-wrap gap-1.5">
							{OUTCOMES.map((o) => (
								<button
									key={o}
									type="button"
									onClick={() => {
										setOutcome(outcome === o ? null : o);
										if (o === "no_answer" && !nextAt)
											setNextAt(toLocalInput(at(1)));
										if (o === "callback" && !nextAt)
											setNextAt(toLocalInput(at(1)));
										if (o === "interested" && !nextAt)
											setNextAt(toLocalInput(at(3)));
									}}
									className={`rounded-full border px-2.5 py-1 text-[12px] transition-colors ${
										outcome === o
											? `${OUTCOME_STYLE[o]} bg-zinc-900 text-white`
											: `${OUTCOME_STYLE[o]} bg-white text-zinc-700 hover:bg-canvas`
									}`}
								>
									{OUTCOME_LABEL[o]}
								</button>
							))}
						</div>
						<textarea
							className={`${input} mt-3 min-h-[90px]`}
							placeholder={
								outcome ? "What was said, who you spoke to" : "Write a note"
							}
							value={note}
							onChange={(e) => setNote(e.target.value)}
						/>
						<div className="mt-3">
							<div className="text-[11px] text-zinc-500">Follow up</div>
							<div className="mt-1 flex flex-wrap gap-1">
								{QUICK_PICKS.map((q) => (
									<button
										key={q.label}
										type="button"
										className="rounded border border-hairline px-2 py-0.5 text-[11px] text-zinc-600 hover:bg-canvas"
										onClick={() => setNextAt(toLocalInput(q.date()))}
									>
										{q.label}
									</button>
								))}
								{nextAt && (
									<button
										type="button"
										className="px-1 text-[11px] text-zinc-400 hover:text-zinc-700"
										onClick={() => setNextAt("")}
									>
										clear
									</button>
								)}
							</div>
							<input
								type="datetime-local"
								className={`${input} mt-1.5`}
								value={nextAt}
								onChange={(e) => setNextAt(e.target.value)}
							/>
						</div>
						<label className="mt-3 flex items-start gap-2 text-[12px] text-zinc-600">
							<input
								type="checkbox"
								className="mt-0.5"
								checked={consent}
								onChange={(e) => setConsent(e.target.checked)}
							/>
							They said yes to a follow-up email
						</label>
						<button
							type="button"
							className={`${btn.primary} mt-3 w-full justify-center`}
							disabled={
								busy ||
								(!outcome && !note.trim()) ||
								(outcome === "callback" && !nextAt)
							}
							onClick={logCall}
						>
							{outcome ? `Log call · ${OUTCOME_LABEL[outcome]}` : "Add note"}
						</button>
						<p className="mt-2 text-[11px] leading-relaxed text-zinc-400">
							Cold email to businesses is not allowed in Denmark, so only email
							someone who agreed to it on the call.
						</p>
					</Panel>

					<Panel title="History">
						{p.activities.length === 0 ? (
							<Empty>No calls or notes yet.</Empty>
						) : (
							<ul>
								{p.activities.map((a) => (
									<ActivityItem
										key={a.id}
										a={a}
										onDelete={() =>
											act(
												() =>
													sendJson<Result>(
														`/prospects/${id}/activities/${a.id}`,
														"DELETE",
													),
												"Note deleted.",
											)
										}
									/>
								))}
							</ul>
						)}
					</Panel>

					<Panel title="Shop signals">
						<dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-[12px]">
							<dt className="text-zinc-500">Platform</dt>
							<dd>
								{p.platform ? (PLATFORM_LABEL[p.platform] ?? p.platform) : "—"}
							</dd>
							<dt className="text-zinc-500">Google Ads tag</dt>
							<dd>
								{signals.google_ads
									? "Yes"
									: signals.gtm
										? "Tag Manager only"
										: "No"}
							</dd>
							<dt className="text-zinc-500">Meta pixel</dt>
							<dd>{signals.meta_pixel ? "Yes" : "No"}</dd>
							<dt className="text-zinc-500">Networks</dt>
							<dd>
								{[p.network, ...(signals.other_networks ?? [])]
									.filter(Boolean)
									.map((x) => NETWORK_LABEL[x as string] ?? x)
									.join(", ") || "—"}
							</dd>
							<dt className="text-zinc-500">Payment</dt>
							<dd>{signals.payment_methods?.join(", ") || "—"}</dd>
							<dt className="text-zinc-500">Trust marks</dt>
							<dd>{signals.trust_marks?.join(", ") || "—"}</dd>
							<dt className="text-zinc-500">Language</dt>
							<dd>{signals.lang ?? "—"}</dd>
						</dl>
						<div className="mt-4 border-t border-hairline pt-3 text-[11px] leading-relaxed text-zinc-500">
							Sitemap: {sitemapFacts.source ?? "—"}
							{sitemapFacts.files ? ` (${sitemapFacts.files} files)` : ""} ·{" "}
							{p.slugMatches}/{p.sampledUrls} URLs matched by name ·{" "}
							{p.pagesRead} pages read, {sample.jsonLdPages ?? 0} with JSON-LD,{" "}
							{p.gtinPages} with a GTIN, {p.gtinMatches} ours · name-match
							precision{" "}
							{sample.precision !== undefined
								? `${Math.round(sample.precision * 100)} %${sample.precisionMeasured ? " (measured)" : " (assumed)"}`
								: "—"}
						</div>
					</Panel>
				</div>
			</div>
		</div>
	);
}
